import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons'
import { SociogramContext } from '@BUP/contexts/SociogramContext'
import { OrganizationContext } from '@BUP/contexts/OrganizationContext'
import { LocationContext } from '@BUP/contexts/LocationContext'
import { useSendActivationSurvey } from '@BUP/hooks/Sociogram/useSendActivationSurvey'
import { findNivel, hasRecommendationsEnabled } from '@BUP/services/BraveUp'
import ButtonComponent from '@BUP/components/Generics/Button'
import BupSpinner from '@BUP/components/Loading/Spinner'
import ModalComponent from '@BUP/components/Generics/Modal'
import IconCheck from '@BUP/assets/Icons/check_icon.svg'
import ParticipantsTerminateds from './participants-terminateds.jsx'
import ActiveSurveyButton from './active-survey-button.jsx'
import ViewResourcesButton from './ViewResourcesButton/index.jsx'
import Table from '..'
import styles from './inner-table.module.scss'

const InnerTable = ({
  getSociogram,
  handleGetRecomendations,
  getSurveys,
  loadingSociogram,
  surveysData,
  sociogram,
  errorResultRecommendations,
  isLoadingDataSurveys
}) => {
  const history = useHistory()
  const { t, i18n } = useTranslation('translation', {
    keyPrefix: 'pages.data.sociogram.activeSociogramEce'
  })

  const { selectedSurvey, setSelectedSurvey, setDataResponseActiveSurvey } =
    useContext(SociogramContext)
  const organization = useContext(OrganizationContext)
  const { type: organizationType } = organization
  const locale = useContext(LocationContext)
  const { locale: lang } = locale
  const isMultiCourse = organizationType === 'multi-course'
  const [stateActiveSurveyModal, setStateActiveSurveyModal] = useState(false)
  const [stateFinishSurveyModal, setStateFinishSurveyModal] = useState(false)
  const [actionSurvey, setActionSurvey] = useState(null)

  const {
    updateSurveySociogram,
    isLoading: loadingResponseSurvey,
    dataResponseSurvey,
    errorResponseSurvey
  } = useSendActivationSurvey(selectedSurvey)

  useEffect(() => {
    if (dataResponseSurvey) {
      setStateActiveSurveyModal(false)
      setStateFinishSurveyModal(false)
      setDataResponseActiveSurvey(dataResponseSurvey)
    }
  }, [dataResponseSurvey, setDataResponseActiveSurvey])

  const handleViewCourseList = (survey) => {
    const surveyId = survey.id || null
    history.push(`/sociogram/students-list/${surveyId}`)
    setSelectedSurvey(surveyId ? survey : null)
  }

  const handleViewCourseResult = async (results, survey) => {
    setSelectedSurvey(survey)

    const surveyId = survey.id
    history.push({
      pathname: `/sociogram/dashboard/${surveyId}`,
      result: results,
      survey
    })
  }

  const handleCloseActiveSurveyModal = () => {
    setSelectedSurvey(null)
    setActionSurvey(null)
    setStateActiveSurveyModal(false)
  }

  const handleShowFinishSurveyModal = (survey) => {
    setStateFinishSurveyModal(true)
    setSelectedSurvey(survey)
  }

  const handleCloseModalFinishSurvey = () => {
    setSelectedSurvey(null)
    setActionSurvey(null)
    setStateFinishSurveyModal(false)
  }

  const handleConfirmActiveSurvey = async () => {
    setActionSurvey('active')
    await updateSurveySociogram({
      data: {
        isActive: true,
        isDone: false
      }
    })
      .then(() => getSociogram())
      .then(() => getSurveys())
      .then(() => handleGetRecomendations())
  }

  const handleConfirmFinishSurvey = async () => {
    setActionSurvey('finish')
    await updateSurveySociogram({
      data: {
        isDone: true,
        isActive: false
      }
    })
      .then(() => getSociogram())
      .then(() => getSurveys())
      .then(() => handleGetRecomendations())
  }

  const setFontColor = (isActive, isDone, results) => {
    if (!isActive && isDone && results && results.length === 0) {
      return '#17BEBB'
    }
    if (!isActive && isDone && results && results.length > 0) return '#6441A5'
    else return '#898A89'
  }

  const setFontWeight = (isActive, isDone, results) => {
    if (!isActive && isDone && results && results.length > 0) return 'bold'
    if (!isActive && isDone && results && results.length === 0) return 'bold'
    else return '400'
  }

  const columns = [
    {
      Header: '',
      accessor: 'id',
      disableSortBy: true,
      Cell: ({ row }) => {
        if (!row) return <div className={styles.icon_cell} />
        const { original: survey } = row
        if (!survey) return <div className={styles.icon_cell} />
        const { isDone, results } = survey
        if (isDone && results && results.length > 0) {
          return (
            <div className={styles.icon_cell}>
              <div>
                <img src={IconCheck} alt='Icon check' />
              </div>
            </div>
          )
        }
        return <div className={styles.icon_cell} />
      }
    },
    {
      Header: isMultiCourse ? i18n.t('common.resources.course.attributes.tags.nivel.label') : i18n.t('common.resources.course.label'),
      accessor: 'name',
      Cell: ({ row }) => {
        if (!row) return <div className={styles.name_cell} />
        const { original: survey } = row
        if (!survey) return <div className={styles.name_cell} />
        const { isActive, isDone, results, courses } = survey
        const [course] = courses
        const nivel = course.tags.find(tag => tag.startsWith('nivel'))
        const splitNivel = nivel && nivel.split(':')[1]
        const surveyName = isMultiCourse ? i18n.t(`common.resources.course.values.tags.nivel.${splitNivel}`) : course.name

        return (
          <div className={styles.name_cell}>
            <span
              style={{
                color: setFontColor(isActive, isDone, results),
                fontWeight: setFontWeight(isActive, isDone, results)
              }}
              className={styles.name_text}
            >
              {surveyName}
            </span>
          </div>
        )
      }
    },
    {
      Header: `${t('table.headerState')}`,
      accessor: 'isActive',
      disableSortBy: true,
      Cell: ({ value: isActive, row }) => {
        const { original: survey } = row
        if (!row) return <p>No row</p>
        if (!survey) return <p>{t('noSurveyMessage')}</p>
        const { isDone, results } = survey
        if (isActive && !isDone) {
          return (
            <span
              className={styles.status_text}
              style={{
                color: setFontColor(isActive, isDone, results),
                fontWeight: setFontWeight(isActive, isDone, results)
              }}
            >
              {t('table.activeState')}
            </span>
          )
        }
        if (!isActive && isDone) {
          return (
            <span
              className={styles.status_text}
              style={{
                color: setFontColor(isActive, isDone, results),
                fontWeight: setFontWeight(isActive, isDone, results)
              }}
            >
              {t('table.completedState')}
            </span>
          )
        }
        if (!isActive && isDone) {
          return (
            <span
              className={styles.status_text}
              style={{
                color: setFontColor(isActive, isDone, results),
                fontWeight: setFontWeight(isActive, isDone, results)
              }}
            >
              {t('table.closedState')}
            </span>
          )
        }
        return (
          <span
            className={styles.status_text}
            style={{
              color: setFontColor(isActive, isDone, results),
              fontWeight: setFontWeight(isActive, isDone, results)
            }}
          >
            {t('table.notActivatedState')}
          </span>
        )
      }
    },
    {
      Header: `${t('table.headerResponses')}`,
      accessor: 'participants',
      disableSortBy: true,
      Cell: ({ row, value: participants }) => {
        if (!row) return <p>No row</p>
        const { original: survey } = row
        if (!survey) return <p>{t('noSurveyMessage')}</p>
        const { id: surveyId, isActive, isDone, results } = survey
        if (!participants || participants.length === 0) {
          return <p>--</p>
        }

        const numberOfParticipants = participants.length
        return (
          <div className={styles.responses_cell_wrapper}>
            <span
              style={{
                color: setFontColor(isActive, isDone, results),
                fontWeight: 'bold'
              }}
              className={styles.responses_text}
            >
              <span>{t('table.responsesText')}</span>
              <div>
                <ParticipantsTerminateds surveyId={surveyId} /> /{' '}
                {numberOfParticipants}
              </div>
            </span>
            <ButtonComponent
              buttonText={`${t('table.listButton')}`}
              colorType='primary'
              icon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
              size='small'
              bold={false}
              onClick={() => {
                handleViewCourseList(survey)
              }}
              hideTextInMobile
              disabled={!surveyId}
            />
          </div>
        )
      }
    },
    {
      Header: `${t('table.actionsHeader')}`,
      accessor: 'isDone',
      disableSortBy: true,
      Cell: ({ value: isDone, row }) => {
        const { original: survey } = row
        if (!row) return <p>No row</p>
        if (!survey) return <p>{t('noSurveyMessage')}</p>
        const { isActive, results, resource, courses } = survey
        const result = results?.length > 0

        if (loadingResponseSurvey || loadingSociogram || isLoadingDataSurveys) {
          return <BupSpinner size='small' />
        }
        if (!isActive && !isDone && !result) {
          return (
            <ActiveSurveyButton
              setSelectedSurvey={setSelectedSurvey}
              setStateActiveSurveyModal={setStateActiveSurveyModal}
              sociogram={sociogram}
              survey={survey}
            />
          )
        }
        if (isActive && !isDone && !result) {
          return (
            <div
              className={styles.actions_cell_wrapper}
              data-test='cell-wrapper-finish-button'
            >
              <ButtonComponent
                buttonText={`${t('table.finishButton')}`}
                colorType='secondary'
                block
                bold={false}
                onClick={() => handleShowFinishSurveyModal(survey)}
              />
            </div>
          )
        }
        const surveyId = survey.id
        const course = courses[0]
        const nivel = findNivel(lang, course)
        const surveyName = isMultiCourse ? nivel.name : course.name

        // TODO SOCIOGRAM: Fix conditions to show button by finished survey
        if (result && !isActive && isDone) {
          if (results.length === 1) {
            const filterResult = results.find(
              (result) => result.survey === surveyId
            )
            const isCompletedResult = results[0]?.isCompleted
            return (
              <div
                className={styles.actions_cell_wrapper}
                data-test='cell-wrapper-simple-results-button'
              >
                <ButtonComponent
                  buttonText={`${t('table.resultsButton')}`}
                  colorType='secondary'
                  block
                  bold={false}
                  onClick={() => handleViewCourseResult(filterResult, survey)}
                  disabled={!isCompletedResult}
                />
                {hasRecommendationsEnabled(organization)
                  ? <ViewResourcesButton
                      buttonText={`${t('table.resourcesButton')}`}
                      resource={resource}
                      errorResultRecommendations={errorResultRecommendations}
                      surveyName={surveyName}
                    />
                  : null}

              </div>
            )
          } else {
            return (
              <div
                className={styles.actions_cell_wrapper}
                data-test='cell-wrapper-multi-results-button'
              >
                <ButtonComponent
                  buttonText={`${t('table.resultsButton')}`}
                  colorType='secondary'
                  block
                  bold={false}
                  onClick={() => handleViewCourseResult(results, survey)}
                  // disabled={isCompletedResult}
                />
              </div>
            )
          }
        }
        return <p>{t('noResultsMessage')}</p>
      }
    }
  ]

  return (
    <>
      {/* Modal Activar survey */}
      <ModalComponent
        stateModal={stateActiveSurveyModal}
        onClickCloseModal={() => {
          handleCloseActiveSurveyModal()
        }}
        modalTitle={`${t('activationSurveyModal.title')} ${
          selectedSurvey?.name
        }`}
        modalDescription={
          <div className={styles.modal_description_wrapper}>
            {t('activationSurveyModal.description')}
            {actionSurvey === 'active' &&
              errorResponseSurvey &&
              errorResponseSurvey.statusCode === 400 && (
                <span className={styles.error_text}>
                  {t('activationSurveyModal.notEnoughtParticipantsError')}
                </span>
            )}
          </div>
        }
        onClickConfirm={() => handleConfirmActiveSurvey()}
        loading={loadingResponseSurvey || loadingSociogram}
        dataTest='active-survey'
      />
      {/* Finish survey modal */}
      <ModalComponent
        stateModal={stateFinishSurveyModal}
        onClickCloseModal={() => {
          handleCloseModalFinishSurvey()
        }}
        modalTitle={`${t('completionSurveyModal.title')} ${
          selectedSurvey?.name
        }`}
        modalDescription={
          <div className={styles.modal_description_wrapper}>
            {t('completionSurveyModal.description')}
            {actionSurvey === 'finish' &&
              errorResponseSurvey &&
              errorResponseSurvey.statusCode === 400 && (
                <span className={styles.error_text}>
                  {t(
                    'completionSurveyModal.notEnoughtParticipantsTerminatedError'
                  )}
                </span>
            )}
          </div>
        }
        onClickConfirm={() => handleConfirmFinishSurvey()}
        loading={loadingResponseSurvey}
        dataTest='finish-survey'
      />
      <Table data={surveysData} columnsArray={columns} smallFirstChild />
    </>
  )
}

export default InnerTable
